import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Select from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import RegisterField, { FieldProps } from '../register-field';
import { SelectProps as MuiSelectProps } from '@mui/material';

type Option = {
  value: string | number;
  label: string;
};

type MultiSelectChipProps = FieldProps<MuiSelectProps> & {
  options: Option[];
  label?: string;
  placeholder?: string;
  loading?: boolean;
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function FormMultiSelectChip({
  name,
  options,
  label,
  placeholder = 'Seleccionar...',
  registerOptions,
  disabled,
  loading = false,
  ...props
}: MultiSelectChipProps) {
  const theme = useTheme();

  return (
    <RegisterField name={name} disabled={disabled} {...registerOptions}>
      {(field) => (
        <FormControl fullWidth error={field.hasError}>
          {label && <InputLabel id={`${name}-label`}>{label}</InputLabel>}
          <>
            <Select
              multiple
              labelId={`${name}-label`}
              id={name}
              value={field.value || []}
              disabled={field.disabled || loading}
              IconComponent={loading ? () => null : undefined}
              input={
                <OutlinedInput
                  id={`${name}-input`}
                  label={label}
                  placeholder={!field.value?.length ? placeholder : ''}
                  error={field.hasError}
                  endAdornment={
                    loading ? <CircularProgress color="inherit" size={20} /> : null
                  }
                />
              }
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
                  {selected.map((value: string | number) => {
                    const selectedOption = options.find((opt) => opt.value === value);
                    return selectedOption ? (
                      <Chip
                        key={value}
                        label={selectedOption.label}
                        onDelete={() => {
                          const newValue = field.value.filter((v: string | number) => v !== value);
                          field.handleChange(newValue);
                        }}
                        onMouseDown={(event) => {
                          event.stopPropagation();
                        }}
                      />
                    ) : null;
                  })}
                </Box>
              )}
              MenuProps={MenuProps}
              onChange={(e) => {
                const { value } = e.target;
                field.handleChange(value);
              }}
              onBlur={field.handleBlur}
              onKeyDown={field.handleKeyDown}
              error={field.hasError}
              {...props}
            >
              {options.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{
                    fontWeight: (field.value || []).includes(option.value)
                      ? theme.typography.fontWeightMedium
                      : theme.typography.fontWeightRegular,
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {field.hasError && (
              <FormHelperText error>{field.errorMessage}</FormHelperText>
            )}
          </>
        </FormControl>
      )}
    </RegisterField>
  );
}
